import { t } from '@lingui/core/macro';
import { HeaderKeys } from '@watershed/shared-universal/utils/constants';
import { getPinnedOrganizationId } from './pinnedOrganizationId';

export default function wsFetch(
  input: RequestInfo | URL,
  init?: RequestInit | undefined
): Promise<Response> {
  const organizationId = getPinnedOrganizationId();
  let headers = { ...init?.headers };
  if (organizationId) {
    // might not always be defined (i.e. if called from admin)
    headers = { ...headers, [HeaderKeys.activeOrganizationId]: organizationId };
  }
  /* eslint-disable @watershed/must-use-wsfetch */
  return fetch(input, {
    ...init,
    headers,
  });
}

/**
 * Error thrown when a fetch response has HTTP 401 status.
 * Callers can use `instanceof` to detect auth failures without
 * relying on fragile message-text matching.
 */
export class AuthenticationError extends Error {
  constructor() {
    super(
      t({
        message:
          'Your session has expired. Please refresh the page to log in again.',
        context: 'Error message shown when user session expires during AI chat',
      })
    );
    this.name = 'AuthenticationError';
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

/**
 * Fetch wrapper that throws {@link AuthenticationError} on 401 responses.
 * Use this for non-GraphQL endpoints (REST, SSE) where the urql
 * errorExchange 401 handling does not apply.
 *
 * @throws {AuthenticationError} When the response status is 401.
 */
export async function authAwareFetch(
  input: RequestInfo | URL,
  init?: RequestInit | undefined
): Promise<Response> {
  const response = await wsFetch(input, init);
  if (response.status === 401) {
    throw new AuthenticationError();
  }
  return response;
}
